import { MouseEventHandler, useEffect } from 'react'
import { colors } from '@fitpet/constants-global'
import { FitpetIcon } from '@fitpet/ui/components'
import styled from 'styled-components'
import { ProductCategoryScheme, SupplyTypeEnum } from '@/apis/rest/generated/types'
import { Image } from '@/components/common/images/Image'
import { Typo } from '@/components/common/typography'
import { ProductCardList } from '@/components/domains/product-detail/ProductDetailBrand/components/product-card-list'
import { ENV_CONFIG } from '@/constants/env/config'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { LocationEnum } from '@/constants/location.enum'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { useCustomRouter, usePetType } from '@/containers/hooks'
import { UseCustomRouterType } from '@/containers/hooks/useCustomRouter'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { getImageUrl } from '@/utils/utilURL'

interface ProductDetailBrandProps {
  /** 브랜드 ID */
  brandId: number
  /** 브랜드 이름 */
  brandName: string
  /** 브랜드 로고 URL */
  brandLogoUrl?: string
  /** 제품 ID */
  productId: number
  /** 제품 이름 */
  productName: string
  /** 품절 여부 */
  isRealSoldOut: boolean
  /** 가격 */
  price: number
  /** 할인율 */
  discountRate: number
  /** 할인 가격 */
  salePrice: number
  /** 리뷰 점수 */
  reviewScore: number
  /** 리뷰 수 */
  reviewCount: number
  /** 타임세일 여부 */
  isTimeSale: boolean
  /** 제품 카테고리 */
  categories: ProductCategoryScheme[]
  /** 공급 유형 */
  supplyType: SupplyTypeEnum
}

export const ProductDetailBrand = ({
  brandId,
  brandName,
  brandLogoUrl = `${ENV_CONFIG.fitpetmallHost}/images/product_placeholder_76.svg`,
  productId,
  productName,
  isRealSoldOut,
  price,
  discountRate,
  salePrice,
  reviewScore,
  reviewCount,
  isTimeSale,
  categories,
  supplyType,
}: ProductDetailBrandProps) => {
  const router: UseCustomRouterType = useCustomRouter()
  const { getPetType } = usePetType()
  const { basePath, asPath } = useCustomRouter()

  const { trackViewProductDetailBrand } = useProductDetailEventTracker()
  const { trackClickProductDetailBrandButton, trackViewProductDetailMiddleProductBrand } =
    useProductDetailEventTracker()

  const handleClickBrand: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()

    trackClickProductDetailBrandButton({
      brandId,
      brandName,
      productId,
      productName,
      isRealSoldOut,
      price,
      discountRate,
      salePrice,
      reviewScore,
      reviewCount,
      isTimeSale,
      categories,
      supplyType,
      listType: 'samebrandproduct',
      listSection: 'samebrand',
      sectionName: `${brandName}의 인기상품`,
    })

    // 냥쌤 브랜드 화면 신규 MVP개발 과정에서 이벤트 화면을 사용하여 개발함. 이에 따라, 해당 화면으로 링크를 변경함.
    const nyangSsamId = 24
    const adSlug = 'B_nyangssaem_main_cat'
    const currentPath = basePath + asPath
    if (brandId === nyangSsamId) {
      router.push({
        pathname: ROUTES.EVENT_DETAIL,
        query: { adSlug: adSlug, location: LocationEnum.ProductDetail, [QueryStringKeyEnum.PrevPath]: currentPath },
      })
      return
    }

    router.push({
      pathname: ROUTES.BRAND_DETAIL,
      query: {
        brandId: Base64ConverterUtil.convertNumberIdToBase64ByKey({ key: 'BrandType', id: brandId }),
        location: LocationEnum.ProductDetail,
      },
    })
  }

  useEffect(() => {
    trackViewProductDetailBrand({
      productId: productId,
      productName: productName,
      brandId: brandId,
      brandName: brandName,
      supplyType: supplyType,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    trackViewProductDetailMiddleProductBrand({
      listType: 'samebrandproduct',
      listSection: 'samebrand',
      brandId: brandId,
      brandName: brandName,
      sectionName: `${brandName}의 인기상품`,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const petType = getPetType()
  const brandLogo = getImageUrl(brandLogoUrl) || ''

  return (
    <StyledRootDiv>
      <StyledBrandButton type="button" onClick={handleClickBrand}>
        <StyledBrandDiv>
          <StyledBrandImage src={brandLogo} alt={`${brandName} 브랜드 이미지`} />
        </StyledBrandDiv>
        <StyledBrandTextDiv>
          <StyledBrandNameSpan>
            <Typo
              variant="Heading6 Bold"
              color={GlobalColorEnum.Gray900}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {brandName}
            </Typo>
          </StyledBrandNameSpan>
          <Typo variant="Heading6 Bold" color={GlobalColorEnum.Gray900}>
            의 인기상품
          </Typo>
        </StyledBrandTextDiv>
        <FitpetIcon icon="icChevronRight" size={24} color={colors.GRAY_900} />
      </StyledBrandButton>
      {brandId && petType && (
        <ProductCardList
          brandId={brandId}
          brandName={brandName}
          petType={petType}
          productId={productId}
          productName={productName}
          isRealSoldOut={isRealSoldOut}
          price={price}
          discountRate={discountRate}
          salePrice={salePrice}
          reviewScore={reviewScore}
          reviewCount={reviewCount}
        />
      )}
    </StyledRootDiv>
  )
}

const StyledRootDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.6rem 0 2.4rem 0;
  gap: 1.6rem;
`

const StyledBrandButton = styled.button`
  display: flex;
  padding: 0.8rem 1.2rem;
  margin: 0 1.6rem;
  align-items: center;
  gap: 0.8rem;
  border-radius: 0.8rem;
  border: 1px solid ${GlobalColorEnum.Gray100};
  background-color: ${GlobalColorEnum.GrayWhite};
  cursor: pointer;
`

const StyledBrandDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`

const StyledBrandImage = styled(Image)`
  width: 4rem;
  height: 4rem;
  flex-shrink: 0;
  border-radius: 4rem;
  border: 1px solid ${GlobalColorEnum.Gray50};
`

const StyledBrandTextDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`

const StyledBrandNameSpan = styled.span`
  display: flex;
  align-items: center;
  max-width: 13.6rem;
`
